// typed data hooks — the single place components get server data. keeps fetching,
// caching, and API shapes out of the UI components.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgendaEntry,
  AgendaEntryInput,
  AssemblyOrderStatus,
  AssemblyOrderStatusInput,
  BoardSummary,
  BoatDocInput,
  BoatDocRecord,
  OrderSheetImportInput,
  OrderSheetSetInput,
  ServiceRequest,
  ServiceRequestDecideInput,
  DeckStageState,
  DeckStatus,
  DsVessel,
  GrpVessel,
  LoadingBulkResponse,
  LoadingItemState,
  LoadingListResponse,
  LoadingPushResponse,
  LoadingSetResponse,
  LoadingStatus,
  OverviewConfirmation,
  OverviewVessel,
  PdiVessel,
  Pin,
  ProductionSection,
  ReportSummary,
  SalesOrder,
  SalesOrderInput,
  SalesOrderSummary,
  ScheduleTask,
  ScheduleTaskInput,
  ScheduleVessel,
  ScheduleVesselInput,
  VesselSummary,
} from '@technohull/shared';
import { LOADING_ITEMS, type ServiceExtraKind } from '@technohull/shared';
import { api } from '../lib/api.js';
import { withPdiStatus } from '../lib/pdi.js';
import { COOP_QUIET } from '../collab/collabHooks.js';
import { useActiveSeason } from '../season/SeasonContext.js';

const seasonQ = (season?: string) => (season ? `?season=${encodeURIComponent(season)}` : '');

export interface DataResponse {
  season: string;
  vessels: VesselSummary[];
  /** this season's boats in the Overview that aren't on an assembly sheet yet (only with overview:read). */
  notOnSheet?: Array<{ sap: string; name: string; model: string }>;
  /** the server answered from its last copy (read at `asOf`) while it reads the sheets again: ask again soon */
  stale?: boolean;
  asOf?: string;
}
/** while the server answers from its last copy, ask again every few seconds until the fresh read is in */
const STALE_RETRY_MS = 4000;
const untilFresh = (q: { state: { data?: { stale?: boolean } } }) => (q.state.data?.stale ? STALE_RETRY_MS : false);
/** the production sheets' vessels. `enabled` lets a page skip the read for a viewer without
 *  production:read (the server would only refuse it). */
export function useProductionVessels(season?: string, enabled = true) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['data', s],
    queryFn: () => api.get<DataResponse>(`/api/data${seasonQ(s)}`),
    enabled,
    refetchInterval: untilFresh,
  });
}

export function useBoard(season?: string) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['board', s],
    queryFn: () => api.get<BoardSummary & { season: string }>(`/api/board${seasonQ(s)}`),
  });
}

/** the manpower & hours report. `enabled` lets a page skip it for a viewer without report:read. */
export function useReport(season?: string, enabled = true) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['report', s],
    queryFn: () => api.get<ReportSummary & { season: string }>(`/api/report${seasonQ(s)}`),
    enabled,
  });
}

export type ReportLogMetrics = Record<
  'hoursCompleted' | 'hoursNeeded' | 'daysAvail' | 'theoretical' | 'actual60' | 'surplus' | 'dailyRate' | 'dailyIdeal' | 'dailyDone' | 'idealDone',
  number | null
>;
export interface ReportLogEntry {
  date: string;
  all: ReportLogMetrics;
  groups: Array<{ team: string } & ReportLogMetrics>;
  models: Array<{ model: string; pctDone: number | null }>;
}
export function useReportLog(season?: string, enabled = true) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['report-log', s],
    queryFn: () =>
      api.get<{ season: string; targetDate: string; today: ReportLogEntry | null; log: ReportLogEntry[] }>(
        `/api/report-log${seasonQ(s)}`,
      ),
    // caller can defer this until it's actually needed (e.g. the Board only shows the log on demand),
    // which avoids the server rebuilding the report a second time (buildReportWithLog → buildReport)
    // and the daily-log KV write on every Board load.
    enabled,
  });
}
/** clear the current season's rolling report log (admin) — drops snapshots recorded before a fix. */
export function useResetReportLog(season?: string) {
  const active = useActiveSeason();
  const s = season ?? active;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/api/report-log/reset${seasonQ(s)}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-log', s] }),
  });
}

export interface OverviewResponse {
  season: string;
  sheetId: string;
  cols: Record<string, number>;
  vessels: OverviewVessel[];
  confirmations: Record<string, OverviewConfirmation>;
  /** per-vessel Assembly order tracking, keyed by SAP (for the Overview hover popover). */
  orderStatus?: Record<string, AssemblyOrderStatus>;
}
/** where each boat stands in build (Vessel Progress): name, SAP, model, assembly % and delivery only, so
 *  Sales can see it without the rest of the Overview */
export type VesselProgressRow = Pick<OverviewVessel, 'rowId' | 'sap' | 'name' | 'model' | 'assemblyPct' | 'deliveryDate' | 'deliveryConfirmed' | 'left'>;
export function useVesselProgress(season?: string) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['vessel-progress', s],
    queryFn: () => api.get<{ season: string; vessels: VesselProgressRow[] }>(`/api/vessel-progress${seasonQ(s)}`),
  });
}

/** `enabled: false` for a page that only uses the Overview when its reader may see it */
export function useOverview(season?: string, { enabled = true }: { enabled?: boolean } = {}) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['overview', s],
    queryFn: () => api.get<OverviewResponse>(`/api/overview${seasonQ(s)}`),
    enabled,
  });
}

export interface CellHistoryEntry {
  value: string;
  by: string;
  email: string;
  at: string; // ISO timestamp
}
export interface CellHistoryResponse {
  history: CellHistoryEntry[];
  available: boolean; // false when Smartsheet's plan doesn't expose cell history
}
/** change history of a single overview cell — fetched lazily (enabled) on hover, cached per cell. */
export function useCellHistory(rowId: number | null, columnId: number | null, season?: string) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['cellHistory', rowId, columnId, s],
    queryFn: () =>
      api.get<CellHistoryResponse>(
        `/api/overview/cell-history?rowId=${rowId}&columnId=${columnId}${s ? `&season=${encodeURIComponent(s)}` : ''}`,
      ),
    enabled: !!rowId && !!columnId,
    staleTime: 60_000,
  });
}
/** change history of a cell on ANY of the app's Smartsheet grids — same shape as useCellHistory but
 *  keyed by the sheetId the grid already holds (Service / GRP / PDI / DS / …). Fetched lazily on hover. */
export function useSheetCellHistory(sheetId: string | null, rowId: number | null, columnId: number | null) {
  return useQuery({
    queryKey: ['sheetCellHistory', sheetId, rowId, columnId],
    queryFn: () =>
      api.get<CellHistoryResponse>(
        `/api/sheet/cell-history?sheetId=${encodeURIComponent(sheetId!)}&rowId=${rowId}&columnId=${columnId}`,
      ),
    enabled: !!sheetId && !!rowId && !!columnId,
    staleTime: 60_000,
  });
}
export function useSetOverviewConfirm() {
  const qc = useQueryClient();
  const key = ['overview', useActiveSeason()]; // must match the visible ['overview', active] slot
  return useMutation({
    mutationFn: (v: { sap: string; field: 'sales' | 'logistics'; value: boolean }) =>
      api.post('/api/overview/confirm', v),
    // reflect the toggle instantly; a full overview refetch (Smartsheet, slow) would otherwise lag.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<OverviewResponse>(key);
      if (prev) {
        const cur = prev.confirmations[v.sap] ?? { sap: v.sap, confirmedSales: false, confirmedLogistics: false, salesBy: '', salesAt: '', logisticsBy: '', logisticsAt: '' };
        const next = { ...cur, ...(v.field === 'sales' ? { confirmedSales: v.value } : { confirmedLogistics: v.value }) };
        qc.setQueryData<OverviewResponse>(key, { ...prev, confirmations: { ...prev.confirmations, [v.sap]: next } });
      }
      return { prev };
    },
    // no post-write refetch — the optimistic update is authoritative; onError rolls back on failure.
    // (The list refreshes on the sync button / window focus / 45s cache expiry.)
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}

// ── assembly order tracking (D1, keyed by SAP) — Order Lists editor + Overview hover ──
export interface AssemblyOrdersResponse {
  orders: Record<string, AssemblyOrderStatus>;
}
export function useAssemblyOrders() {
  return useQuery({
    queryKey: ['assembly-orders'],
    queryFn: () => api.get<AssemblyOrdersResponse>('/api/assembly-orders'),
    staleTime: 30_000,
  });
}
const emptyStatus = (sap: string): AssemblyOrderStatus => ({
  sap, printedAt: '', printedBy: '', leaderChecked: false, leaderBy: '', leaderAt: '', comments: '',
});
export function useSetAssemblyOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: AssemblyOrderStatusInput) =>
      api.post<{ ok: true; status: AssemblyOrderStatus }>('/api/assembly-orders', v),
    // reflect the edit immediately (a checkbox/date shouldn't snap back while the request is in flight).
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['assembly-orders'] });
      const prev = qc.getQueryData<AssemblyOrdersResponse>(['assembly-orders']);
      qc.setQueryData<AssemblyOrdersResponse>(['assembly-orders'], (cur) => {
        const orders = { ...(cur?.orders ?? {}) };
        const s = orders[v.sap] ?? emptyStatus(v.sap);
        orders[v.sap] = {
          ...s,
          ...(v.printedAt !== undefined ? { printedAt: v.printedAt } : {}),
          ...(v.leaderChecked !== undefined ? { leaderChecked: v.leaderChecked } : {}),
          ...(v.comments !== undefined ? { comments: v.comments } : {}),
        };
        return { orders };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['assembly-orders'], ctx.prev); },
    // reconcile with the server's stamped row (who/when), then refresh the Overview hover.
    onSuccess: (res) => {
      qc.setQueryData<AssemblyOrdersResponse>(['assembly-orders'], (prev) =>
        prev ? { orders: { ...prev.orders, [res.status.sap]: res.status } } : { orders: { [res.status.sap]: res.status } },
      );
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

// ── native order sheet (SHEET 27) — editable grid on the Order Lists season view ──
export interface OrderSheetResponse {
  sheet: Record<string, Record<string, string>>; // sap -> { columnLetter: value }
  hidden?: string[]; // SAPs removed from the order sheet (kept out of the grid + procurement)
}
export function useOrderSheet() {
  return useQuery({
    queryKey: ['order-sheet'],
    queryFn: () => api.get<OrderSheetResponse>('/api/order-sheet'),
    staleTime: 30_000,
  });
}
/** remove a vessel from the order sheet (app-side hide; Smartsheet untouched), optimistically. */
export function useDeleteOrderSheetRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sap: string }) => api.post('/api/order-sheet/delete', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['order-sheet'] });
      const prev = qc.getQueryData<OrderSheetResponse>(['order-sheet']);
      qc.setQueryData<OrderSheetResponse>(['order-sheet'], (cur) => {
        const sheet = { ...(cur?.sheet ?? {}) };
        delete sheet[v.sap];
        const hidden = Array.from(new Set([...(cur?.hidden ?? []), v.sap]));
        return { sheet, hidden };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['order-sheet'], ctx.prev); },
  });
}
/** restore a previously removed vessel. */
export function useRestoreOrderSheetRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sap: string }) => api.post('/api/order-sheet/restore', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['order-sheet'] });
      const prev = qc.getQueryData<OrderSheetResponse>(['order-sheet']);
      qc.setQueryData<OrderSheetResponse>(['order-sheet'], (cur) =>
        cur ? { ...cur, hidden: (cur.hidden ?? []).filter((s) => s !== v.sap) } : cur,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['order-sheet'], ctx.prev); },
  });
}
export function useSetOrderSheetCell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: OrderSheetSetInput) => api.post('/api/order-sheet', v),
    // reflect the edit immediately; the cell keeps the typed value without waiting for a refetch.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['order-sheet'] });
      const prev = qc.getQueryData<OrderSheetResponse>(['order-sheet']);
      qc.setQueryData<OrderSheetResponse>(['order-sheet'], (cur) => {
        const sheet = { ...(cur?.sheet ?? {}) };
        sheet[v.sap] = { ...(sheet[v.sap] ?? {}), ...v.cells };
        // keep `hidden` — dropping it un-hides removed vessels, which then wrongly reappear in the
        // Procurement rollup (and the grid) after any cell edit until the next refetch.
        return { sheet, hidden: cur?.hidden };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['order-sheet'], ctx.prev); },
    // reconcile with the server afterwards so the local cache can't drift (mirrors the import flow).
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['order-sheet'] }); },
  });
}
export function useImportOrderSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: OrderSheetImportInput) => api.post<{ ok: true; count: number }>('/api/order-sheet/import', v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['order-sheet'] }); },
  });
}

// ── client service requests (booked through the public portal) ──
export interface ServiceRequestsResponse { requests: ServiceRequest[]; newCount: number }
export function useServiceRequests() {
  return useQuery({
    queryKey: ['service-requests'],
    queryFn: () => api.get<ServiceRequestsResponse>('/api/service-requests'),
    staleTime: 15_000,
  });
}
export function useDecideServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ServiceRequestDecideInput) => api.post<{ request: ServiceRequest; emailed: boolean }>('/api/service-requests/decide', v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['service-requests'] }); },
  });
}

// ── Boat yard entry/exit documents (Service, Documents) ───────────────────────────
export interface BoatDocsResponse { items: BoatDocRecord[]; }
export function useBoatDocs() {
  return useQuery({
    queryKey: ['boat-docs'],
    queryFn: () => api.get<BoatDocsResponse>('/api/boat-docs'),
    staleTime: 15_000,
  });
}
export function useSaveBoatDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: BoatDocInput) => api.post<{ ok: boolean; id: number }>('/api/boat-docs/save', v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['boat-docs'] }); },
  });
}
export function useDeleteBoatDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ ok: boolean }>('/api/boat-docs/delete', { id }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['boat-docs'] }); },
  });
}

// ── assembly weights template (per-model "Model" blocks in the production sheets) ────────────
export interface SheetDiag {
  total: number;
  pcCounts: Record<string, number>;
  head: Array<{ pc: string; name: string; w: string }>;
  boats: Array<{ name: string; sap: string }>;
  slotHits: Array<{ row: number; pc: string; name: string }>;
}
export interface AssemblyTemplateResponse {
  season: string;
  families: Array<import('@technohull/shared').AssemblyTemplateFamily & { ok: boolean; diag?: SheetDiag }>;
}
export function useAssemblyTemplate(season?: string) {
  const active = useActiveSeason();
  const s = season ?? active;
  return useQuery({
    queryKey: ['assembly-template', s],
    queryFn: () => api.get<AssemblyTemplateResponse>(`/api/assembly-template${seasonQ(s)}`),
    staleTime: 30_000,
  });
}
function useTemplateMutation<V>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => api.post(path, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assembly-template'] }),
  });
}
/** edit a template row's weight and/or name (server rejects non-template rows). */
export function useSetTemplateCell() {
  return useTemplateMutation<{ sheetId: string; rowId: number; name?: string; weight?: number }>('/api/assembly-template/cell');
}
/** add a section (under a model) or a task (under a model/section) as the parent's last child. */
export function useAddTemplateRow() {
  return useTemplateMutation<{ sheetId: string; parentRowId: number; kind: 'section' | 'task'; name: string; weight?: number }>('/api/assembly-template/add');
}
/** remove a template row (a section takes its tasks with it). */
export function useDeleteTemplateRow() {
  return useTemplateMutation<{ sheetId: string; rowId: number }>('/api/assembly-template/delete');
}

type MoveVars = { sheetId: string; rowId: number; siblingId: number; above: boolean };
/** pure: return `data` with the dragged task reordered next to its sibling (optimistic UI). */
function reorderTemplate(data: AssemblyTemplateResponse, v: MoveVars): AssemblyTemplateResponse {
  return {
    ...data,
    families: data.families.map((f) => {
      if (String(f.sheetId) !== String(v.sheetId)) return f;
      return {
        ...f,
        models: f.models.map((m) => {
          const src = m.sections.find((s) => s.tasks.some((t) => t.rowId === v.rowId));
          if (!src) return m;
          const task = src.tasks.find((t) => t.rowId === v.rowId)!;
          return {
            ...m,
            sections: m.sections.map((s) => {
              const tasks = s.tasks.filter((t) => t.rowId !== v.rowId);
              if (v.siblingId === s.rowId) return { ...s, tasks: [task, ...tasks] }; // dropped on the section header → top
              const i = tasks.findIndex((t) => t.rowId === v.siblingId);
              if (i < 0) return { ...s, tasks };
              const at = v.above ? i : i + 1;
              return { ...s, tasks: [...tasks.slice(0, at), task, ...tasks.slice(at)] };
            }),
          };
        }),
      };
    }),
  };
}
/** drag-reorder a template task; optimistic, rolls back on error, then re-syncs from the server. */
export function useMoveTemplateRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: MoveVars) => api.post('/api/assembly-template/move', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['assembly-template'] });
      const snaps = qc.getQueriesData<AssemblyTemplateResponse>({ queryKey: ['assembly-template'] });
      for (const [key, data] of snaps) if (data) qc.setQueryData(key, reorderTemplate(data, v));
      return { snaps };
    },
    onError: (_e, _v, ctx) => { ctx?.snaps.forEach(([key, data]) => qc.setQueryData(key, data)); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['assembly-template'] }),
  });
}

// ── overtime roster (Name Manager behind the printable overtime cards) ───────────────────────
export interface OvertimeResponse { employees: string[] }
export function useOvertime() {
  return useQuery({
    queryKey: ['overtime'],
    queryFn: () => api.get<OvertimeResponse>('/api/overtime'),
    staleTime: 30_000,
  });
}
function useOvertimeMutation<V>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => api.post<OvertimeResponse>(path, v),
    onSuccess: (data) => qc.setQueryData(['overtime'], data),
  });
}
export function useAddOvertimeEmployee() { return useOvertimeMutation<{ name: string }>('/api/overtime/add'); }
export function useRemoveOvertimeEmployee() { return useOvertimeMutation<{ name: string }>('/api/overtime/remove'); }
export function useSortOvertime() { return useOvertimeMutation<Record<string, never>>('/api/overtime/sort'); }
export function useImportOvertime() { return useOvertimeMutation<{ names: string[] }>('/api/overtime/import'); }

/** the live refresh the Service page uses: on window focus and every 30s while the tab is visible. */
const LIVE_SYNC = {
  refetchOnWindowFocus: true,
  refetchInterval: 30_000,
  refetchIntervalInBackground: false,
  staleTime: 10_000,
} as const;
/** Loading and PDI move together, so a write on either side refreshes both (an unmounted query, such as
 *  the PDI grid seen from Loading, is only marked stale and refetches when it's next shown). */
function invalidateLoadingAndPdi(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['loading'] });
  void qc.invalidateQueries({ queryKey: ['pdi'] });
}

export function usePdi() {
  const active = useActiveSeason();
  return useQuery({
    queryKey: ['pdi', active],
    queryFn: ({ queryKey }) => api.get<{ vessels: PdiVessel[] }>(`/api/pdi${seasonQ(queryKey[1] as string | undefined)}`),
    // PDI and Loading keep each other up to date, so both refresh on their own (see LIVE_SYNC)
    ...LIVE_SYNC,
  });
}

/** the GRP 2.0 sheet's boats. `enabled` lets a page skip it for a viewer without grp:read. */
export function useGrp(enabled = true) {
  return useQuery({
    queryKey: ['grp'],
    queryFn: () => api.get<{ vessels: GrpVessel[] }>('/api/grp'),
    enabled,
  });
}

/** the GRP sheet as a native grid (mirrors the Smartsheet layout). */
export function useGrpGrid() {
  return useQuery({
    queryKey: ['grp-grid'],
    queryFn: () => api.get<SheetGrid>('/api/grp/grid'),
    staleTime: 30_000,
  });
}
/** write one GRP cell, optimistically (mirrors useServiceWrite). value may be a string[] for a
 *  multi-select (tags) column. */
export function useGrpGridWrite() {
  const qc = useQueryClient();
  const key = ['grp-grid'];
  return useMutation({
    mutationFn: (v: { rowId: number; columnId: number; value: string | boolean | string[] | null }) =>
      api.post('/api/grp/write', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetGrid>(key);
      if (prev) {
        const display = Array.isArray(v.value) ? v.value.join(', ') : (v.value ?? '');
        qc.setQueryData<SheetGrid>(key, {
          ...prev,
          rows: prev.rows.map((r) => (r.id === v.rowId ? { ...r, cells: { ...r.cells, [v.columnId]: display } } : r)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}
export function useGrpAddRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cells: ServiceCell[]; parentId?: number; siblingId?: number; above?: boolean }) =>
      api.post<{ id: number }>('/api/grp/add-row', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grp-grid'] }),
  });
}
/** a boat of the season's Overview with no GRP row yet, with its configurator order's customer and model
 *  (the model as GRP's MODEL dropdown names it, '' when none fits) */
export interface GrpNewBoat { sap: string; customer: string; model: string; orderModel: string }
/** the boats that could go on GRP next (see /api/grp/new-boats), for the Add a boat form */
export function useGrpNewBoats(enabled: boolean) {
  const active = useActiveSeason();
  return useQuery({
    queryKey: ['grp-new-boats', active],
    queryFn: ({ queryKey }) => api.get<{ season: string; boats: GrpNewBoat[] }>(`/api/grp/new-boats${seasonQ(queryKey[1] as string | undefined)}`),
    enabled,
  });
}
/** add a boat to GRP with its QA task rows copied from `templateId` (see /api/grp/add-boat). */
export function useGrpAddBoat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cells: ServiceCell[]; templateId: number | null }) =>
      api.post<{ id: number; tasks: number }>('/api/grp/add-boat', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['grp-grid'] });
      void qc.invalidateQueries({ queryKey: ['grp-new-boats'] });
      // the Overview takes the boat's Name and MODEL from GRP
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}
/** give a boat on GRP the QA task rows it's missing from another boat (see /api/grp/apply-tasks). */
export function useGrpApplyTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { boatId: number; templateId: number; sections?: number[] }) =>
      api.post<{ added: number; existing: number }>('/api/grp/apply-tasks', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grp-grid'] }),
  });
}
export function useGrpDeleteRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { rowId: number }) => api.post('/api/grp/delete-row', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grp-grid'] }),
  });
}

export function useDs() {
  return useQuery({ queryKey: ['ds'], queryFn: () => api.get<{ vessels: DsVessel[]; sheetId: string; cols: { ds: number; programmed: number; comments: number } }>('/api/ds') });
}
export function useDsWrite() {
  const qc = useQueryClient();
  const key = ['ds'];
  return useMutation({
    mutationFn: (v: { rowId: number; ds?: boolean; programmed?: boolean; comment?: string }) =>
      api.post('/api/ds/write', v),
    // optimistic — reflect the checkbox / comment instantly. A full DS refetch (Smartsheet, slow)
    // on every edit was the lag + grid reload; the optimistic patch is authoritative, sync refreshes.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ vessels: DsVessel[] }>(key);
      if (prev) {
        qc.setQueryData<{ vessels: DsVessel[] }>(key, {
          ...prev,
          vessels: prev.vessels.map((x) =>
            x.rowId === v.rowId
              ? {
                  ...x,
                  ...(v.ds !== undefined ? { ds: v.ds } : {}),
                  ...(v.programmed !== undefined ? { programmed: v.programmed } : {}),
                  ...(v.comment !== undefined ? { comments: v.comment } : {}),
                }
              : x,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}

// ── service sheet (native editable Smartsheet grid) ────────────────────────────
export interface SheetGridColumn { id: number; title: string; type: string; options: string[]; primary: boolean; symbol?: string }
export interface SheetGridRow {
  id: number;
  parentId: number | null;
  cells: Record<string, string | boolean>;
  /** per-cell background colour (hex), decoded from the Smartsheet cell format. */
  fmt?: Record<string, string>;
  /** how many files / comments the row carries in Smartsheet (when the sheet was read with meta). */
  attachmentCount?: number;
  commentCount?: number;
  /** build-season id (e.g. "26-27") resolved server-side from the boat this row refers to. */
  season?: string;
}
export interface SheetGrid { sheetId: string; columns: SheetGridColumn[]; rows: SheetGridRow[] }
export type ServiceCellValue = string | boolean | string[] | null;
export type ServiceCell = { columnId: number; value: ServiceCellValue };

export function useServiceSheet(enabled = true) {
  return useQuery({
    queryKey: ['service'],
    queryFn: () => api.get<SheetGrid>('/api/service'),
    enabled,
    // Keep the floor in sync with Smartsheet and with other editors: pull a fresh grid when the tab
    // regains focus and on a gentle poll while it's visible. Optimistic writes cancelQueries on mutate,
    // so a background refresh never clobbers an edit in flight.
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}
/** make sure the Service sheet has its "Expected date" / "Flag" column, adding it to the Smartsheet the
 *  first time (POST /api/service/ensure-column). The new column joins the cached grid straight away, so
 *  the write that follows shows at once. */
export function useServiceEnsureColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: ServiceExtraKind) => api.post<{ column: SheetGridColumn; created: boolean }>('/api/service/ensure-column', { kind }),
    onSuccess: ({ column }) => {
      qc.setQueryData<SheetGrid>(['service'], (g) => (g && !g.columns.some((c) => c.id === column.id) ? { ...g, columns: [...g.columns, column] } : g));
    },
  });
}
// Service writes run one at a time, in the order they were made (a shared mutation scope), so a later edit
// of a cell can never be overtaken by an earlier one and the last edit is what Smartsheet keeps.
const SERVICE_WRITES = { id: 'service-writes' };
export function useServiceWrite() {
  const qc = useQueryClient();
  const key = ['service'];
  return useMutation({
    scope: SERVICE_WRITES,
    mutationFn: (v: { rowId: number; columnId: number; value: ServiceCellValue }) =>
      api.post('/api/service/write', v),
    // optimistic — reflect the cell instantly; a slow Smartsheet write would otherwise lag/flicker.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetGrid>(key);
      if (prev) {
        // cells are stored as string|boolean, so a tag list is flattened to a comma-joined string.
        const optimistic = Array.isArray(v.value) ? v.value.join(', ') : v.value ?? '';
        qc.setQueryData<SheetGrid>(key, {
          ...prev,
          rows: prev.rows.map((r) => (r.id === v.rowId ? { ...r, cells: { ...r.cells, [v.columnId]: optimistic } } : r)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    // reconcile with the server after the write lands, so the cell shows exactly what Smartsheet stored
    // (normalised dates, picklist casing, …) and any concurrent edit that an error-rollback may have
    // clobbered is restored. The optimistic patch means the user sees no lag while this runs.
    onSettled: () => { void qc.invalidateQueries({ queryKey: key }); },
  });
}
/** several cells of one Service row in one write (the Gantt's Start + Finish), shown at once. */
export function useServiceWriteCells() {
  const qc = useQueryClient();
  const key = ['service'];
  return useMutation({
    scope: SERVICE_WRITES,
    mutationFn: (v: { rowId: number; cells: Array<{ columnId: number; value: string | boolean | null }> }) =>
      api.post('/api/service/write-cells', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetGrid>(key);
      if (prev) {
        const patch: Record<number, string | boolean> = {};
        for (const c of v.cells) patch[c.columnId] = c.value ?? '';
        qc.setQueryData<SheetGrid>(key, { ...prev, rows: prev.rows.map((r) => (r.id === v.rowId ? { ...r, cells: { ...r.cells, ...patch } } : r)) });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => { void qc.invalidateQueries({ queryKey: key }); },
  });
}
export function useServiceAddRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cells: ServiceCell[]; parentId?: number }) => api.post<{ id: number }>('/api/service/add-row', v),
    onSettled: () => qc.invalidateQueries({ queryKey: ['service'] }),
  });
}
/** a boat back for another visit: a new vessel at the top of its group (routes/service.ts serviceNewVisitRoute) */
export function useServiceNewVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { rowId: number }) => api.post<{ id: number; season: string | null; flagged: boolean }>('/api/service/new-visit', v),
    onSettled: () => qc.invalidateQueries({ queryKey: ['service'] }),
  });
}
export function useServiceDeleteRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { rowId: number }) => api.post('/api/service/delete-row', v),
    onSettled: () => qc.invalidateQueries({ queryKey: ['service'] }),
  });
}

// ── deck-flooring Smartsheet (the "Sheet" tab, native editable grid) ─────────────
export function useDeckGrid() {
  return useQuery({ queryKey: ['deck-grid'], queryFn: () => api.get<SheetGrid>('/api/deck/grid'), staleTime: 30_000 });
}
export function useDeckGridWrite() {
  const qc = useQueryClient();
  const key = ['deck-grid'];
  return useMutation({
    mutationFn: (v: { rowId: number; columnId: number; value: ServiceCellValue }) => api.post('/api/deck/write', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetGrid>(key);
      if (prev) {
        const optimistic = Array.isArray(v.value) ? v.value.join(', ') : v.value ?? '';
        qc.setQueryData<SheetGrid>(key, {
          ...prev,
          rows: prev.rows.map((r) => (r.id === v.rowId ? { ...r, cells: { ...r.cells, [v.columnId]: optimistic } } : r)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}
export function useDeckAddRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cells: ServiceCell[]; parentId?: number }) => api.post<{ id: number }>('/api/deck/add-row', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deck-grid'] }),
  });
}
export function useDeckDeleteRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { rowId: number }) => api.post('/api/deck/delete-row', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deck-grid'] }),
  });
}
/** manually fire the "deck cap fitted" notification for a batch of vessels (marks their deck-cap stage
 *  done and emails the configured recipients — the same effect as the assembly task being checked). */
export function useDeckNotify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { saps: string[] }) =>
      api.post<{ ok: boolean; triggered: number; recipients: number; reason?: string }>('/api/deck/notify', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deck'] }),
  });
}

export interface VesselDetail {
  sap: string;
  name: string;
  model: string;
  family: string;
  sheetId: string;
  /** the boat's own row in the production sheet (renamed from Assembly Progress) */
  rowId?: number;
  pct: number;
  remaining: number;
  sections: ProductionSection[];
  /** answered from the server's last copy (read at `asOf`): it asks again shortly */
  stale?: boolean;
  asOf?: string;
}
/** one boat's checklist. `boat` (its own row on the sheet) is what finds it: a SAP can be blank or shared. */
export function useVesselDetail(v: { sap: string; boat?: number } | null, season?: string) {
  const active = useActiveSeason();
  const s = season ?? active;
  const id = v ? v.boat ?? v.sap : null;
  return useQuery({
    queryKey: ['vessel', id, s],
    queryFn: () => api.get<VesselDetail>(`/api/vessel?sap=${encodeURIComponent(v!.sap)}${v!.boat ? `&row=${v!.boat}` : ''}${s ? `&season=${encodeURIComponent(s)}` : ''}`),
    enabled: v != null && (!!v.boat || !!v.sap),
    refetchInterval: untilFresh,
  });
}
/** the list entry a write is about: by its own row when known, else by SAP. */
const sameBoat = (x: VesselSummary, v: { sap: string; boat?: number }) => (v.boat ? x.rowId === v.boat : x.sap === v.sap);

// ── mutations ─────────────────────────────────────────────────────────────────
/** rename a production row from Assembly Progress (the boat, a section heading or a task). The new name
 *  shows at once in the cached checklist and list; a failure puts the old one back. */
export function useRenameProductionRow(season?: string) {
  const qc = useQueryClient();
  const active = useActiveSeason();
  const seasonKey = season ?? active;
  return useMutation({
    mutationFn: (v: { sap: string; boat?: number; rowId: number; name: string }) => api.post('/api/task/rename', { ...v, season: seasonKey }),
    onMutate: async (v) => {
      const detailKey = ['vessel', v.boat ?? v.sap, seasonKey];
      const listKey = ['data', seasonKey];
      await Promise.all([qc.cancelQueries({ queryKey: ['vessel', v.boat ?? v.sap] }), qc.cancelQueries({ queryKey: listKey })]);
      const prevDetail = qc.getQueryData<VesselDetail>(detailKey);
      const prevList = qc.getQueryData<DataResponse>(listKey);
      const isBoat = prevDetail?.rowId === v.rowId;
      if (prevDetail) {
        qc.setQueryData<VesselDetail>(detailKey, {
          ...prevDetail,
          name: isBoat ? v.name : prevDetail.name,
          sections: prevDetail.sections.map((s) => ({
            ...s,
            name: s.r === v.rowId ? v.name : s.name,
            tasks: s.tasks.map((t) => (t.r === v.rowId ? { ...t, n: v.name } : t)),
          })),
        });
      }
      if (prevList && isBoat) {
        qc.setQueryData<DataResponse>(listKey, { ...prevList, vessels: prevList.vessels.map((x) => (sameBoat(x, v) ? { ...x, name: v.name } : x)) });
      }
      return { prevDetail, prevList, detailKey, listKey };
    },
    onError: (_e, _v, c) => {
      if (c?.prevDetail) qc.setQueryData(c.detailKey, c.prevDetail);
      if (c?.prevList) qc.setQueryData(c.listKey, c.prevList);
    },
  });
}

export function useToggleTask(season?: string) {
  const qc = useQueryClient();
  const active = useActiveSeason();
  const seasonKey = season ?? active;
  return useMutation({
    mutationFn: (v: { sap: string; boat?: number; rowId: number; done: boolean }) =>
      api.post('/api/task', { ...v, season: seasonKey }),
    // flip the checkbox instantly (patch the cached detail + list), so you can keep ticking without
    // waiting on the Smartsheet round-trip. no refetch — sync pulls the authoritative state.
    onMutate: async (v) => {
      const detailKey = ['vessel', v.boat ?? v.sap, seasonKey];
      const listKey = ['data', seasonKey];
      // cancel BOTH the detail and the list query — an in-flight refetch of either would otherwise
      // land after this optimistic patch and clobber it (the list count especially).
      await Promise.all([
        qc.cancelQueries({ queryKey: ['vessel', v.boat ?? v.sap] }),
        qc.cancelQueries({ queryKey: listKey }),
      ]);
      const prevDetail = qc.getQueryData<VesselDetail>(detailKey);
      const prevList = qc.getQueryData<DataResponse>(listKey);
      let newPct: number | null = null;
      let newRemaining: number | null = null;
      if (prevDetail) {
        let toggledW = 0;
        const sections = prevDetail.sections.map((s) => ({
          ...s,
          tasks: s.tasks.map((t) => (t.r === v.rowId ? ((toggledW = t.w), { ...t, d: v.done }) : t)),
        }));
        let tot = 0;
        let done = 0;
        for (const s of sections) for (const t of s.tasks) { tot += t.w; if (t.d) done += t.w; }
        newPct = tot > 0 ? Math.round((done / tot) * 100) : prevDetail.pct;
        newRemaining = Math.max(0, Math.round((prevDetail.remaining + (v.done ? -toggledW : toggledW)) * 10) / 10);
        qc.setQueryData<VesselDetail>(detailKey, { ...prevDetail, sections, pct: newPct, remaining: newRemaining });
      }
      if (prevList) {
        qc.setQueryData<DataResponse>(listKey, {
          ...prevList,
          vessels: prevList.vessels.map((x) =>
            sameBoat(x, v)
              ? { ...x, taskNdone: Math.min(x.taskN, Math.max(0, x.taskNdone + (v.done ? 1 : -1))), pct: newPct ?? x.pct, remaining: newRemaining ?? x.remaining }
              : x,
          ),
        });
      }
      return { prevDetail, prevList, detailKey, listKey };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
      if (ctx?.prevList) qc.setQueryData(ctx.listKey, ctx.prevList);
    },
  });
}

export interface CellWrite {
  columnId: number;
  value?: unknown;
  format?: string;
}
export function useOverviewWrite(season?: string) {
  const qc = useQueryClient();
  const active = useActiveSeason();
  const s = season ?? active;
  const key = ['overview', s];
  return useMutation({
    mutationFn: (v: { sheetId: string; rowId: number; cells: CellWrite[] }) =>
      api.post('/api/overview/write', { ...v, season: s }),
    // optimistically apply the edit to the cached row (map columnId → field), so the dropdown /
    // date / checkbox update immediately instead of waiting on the slow Smartsheet round-trip.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<OverviewResponse>(key);
      if (prev) {
        const fieldByCol = new Map(Object.entries(prev.cols).map(([f, id]) => [Number(id), f]));
        qc.setQueryData<OverviewResponse>(key, {
          ...prev,
          vessels: prev.vessels.map((ve) => {
            if (ve.rowId !== v.rowId) return ve;
            const patch = { ...ve } as Record<string, unknown>;
            for (const c of v.cells) {
              const f = fieldByCol.get(Number(c.columnId));
              if (f) patch[f] = c.value == null ? '' : c.value;
            }
            return patch as unknown as OverviewVessel;
          }),
        });
      }
      return { prev };
    },
    // no post-write refetch — the optimistic update above is authoritative. refetching the whole
    // overview from Smartsheet on every edit was the slowness (and blocked the grid). sync refreshes.
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}

export function usePdiWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sheetId: string; rowId: number; cells: CellWrite[] }) =>
      api.post('/api/pdi/write', v),
    // a PDI Status edit also moves the matching Loading item, so both pages refresh
    onSuccess: () => invalidateLoadingAndPdi(qc),
  });
}

/** PDI and Loading refresh once a run of one-tap Status changes has settled, not after every tap: each
 *  refetch re-reads the whole season's PDI. */
let pdiRefreshTimer: ReturnType<typeof setTimeout> | undefined;
function refreshPdiSoon(qc: ReturnType<typeof useQueryClient>) {
  clearTimeout(pdiRefreshTimer);
  pdiRefreshTimer = setTimeout(() => invalidateLoadingAndPdi(qc), 1500);
}

/** one tap on a PDI row's Status (the Holds view): the season's cached PDI list changes at once
 *  (withPdiStatus), the change goes to Smartsheet, and PDI + Loading refresh once the taps settle. A
 *  failed save refreshes straight away, so the row goes back to what the sheet holds. */
export function usePdiStatus() {
  const qc = useQueryClient();
  const active = useActiveSeason();
  return useMutation({
    mutationFn: (v: { sheetId: string; rowId: number; columnId: number; status: string }) =>
      api.post('/api/pdi/write', { sheetId: v.sheetId, rowId: v.rowId, cells: [{ columnId: v.columnId, value: v.status || null }] }),
    onMutate: async (v) => {
      // a refetch already on its way would bring back the row as it was
      await qc.cancelQueries({ queryKey: ['pdi', active] });
      qc.setQueryData<{ vessels: PdiVessel[] }>(['pdi', active], (d) =>
        d ? { ...d, vessels: withPdiStatus(d.vessels, v.sheetId, v.rowId, v.status) } : d,
      );
    },
    onError: () => { clearTimeout(pdiRefreshTimer); invalidateLoadingAndPdi(qc); },
    onSuccess: () => refreshPdiSoon(qc),
  });
}

// ── PDI full list — the raw Smartsheet sheet(s), same grid shape the Service page uses ────
export interface PdiSheetMeta { id: string; label: string; colStatus: number; colComments: number }
/** a model's SAMPLE checklist on a PDI sheet, which a new boat is copied from */
export interface PdiSampleOption { rowId: number; model: string; parentId: number | null; rows: number }
export interface PdiSamplesResponse {
  season: string;
  sheets: Array<{ sheetId: string; label: string; samples: PdiSampleOption[] }>;
  /** the season's Overview boats not on a PDI sheet yet (with overview:read) */
  boats: Array<{ sap: string; name: string; model: string }>;
}
/** the season's PDI sheets' SAMPLEs and the boats that could be added, for the Add a boat dialog */
export function usePdiSamples(enabled: boolean) {
  const active = useActiveSeason();
  return useQuery({
    queryKey: ['pdi', 'samples', active],
    queryFn: ({ queryKey }) => api.get<PdiSamplesResponse>(`/api/pdi/samples${seasonQ(queryKey[2] as string | undefined)}`),
    enabled,
  });
}
/** put a boat on a PDI sheet, copying its model's SAMPLE checklist */
export function useAddPdiBoat() {
  const qc = useQueryClient();
  const active = useActiveSeason();
  return useMutation({
    mutationFn: (v: { sheetId: string; sampleId: number; sap: string; name: string }) =>
      api.post<{ ok: true; id: number; rows: number; sheet: string; model: string }>(`/api/pdi/add-boat${seasonQ(active)}`, v),
    onSuccess: () => invalidateLoadingAndPdi(qc),
  });
}

export function usePdiSheets() {
  const active = useActiveSeason();
  return useQuery({
    queryKey: ['pdi', 'sheets', active],
    queryFn: ({ queryKey }) => api.get<{ sheets: PdiSheetMeta[] }>(`/api/pdi/sheets${seasonQ(queryKey[2] as string | undefined)}`),
    staleTime: 5 * 60_000,
  });
}
export function usePdiSheetGrid(sheetId: string | null) {
  return useQuery({
    queryKey: ['pdi', 'sheet', sheetId],
    queryFn: () => api.get<SheetGrid>(`/api/pdi/sheet?id=${encodeURIComponent(sheetId!)}`),
    enabled: !!sheetId,
    // the whole sheet is thousands of rows, so it refreshes at half the pace of the rest of PDI
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}
/** write one cell of a PDI sheet grid, optimistically (mirrors useServiceWrite). */
export function usePdiGridWrite(sheetId: string | null) {
  const qc = useQueryClient();
  const key = ['pdi', 'sheet', sheetId];
  return useMutation({
    mutationFn: (v: { rowId: number; columnId: number; value: string | boolean | null }) =>
      api.post('/api/pdi/write', { sheetId, rowId: v.rowId, cells: [{ columnId: v.columnId, value: v.value }] }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetGrid>(key);
      if (prev) {
        qc.setQueryData<SheetGrid>(key, {
          ...prev,
          rows: prev.rows.map((r) => (r.id === v.rowId ? { ...r, cells: { ...r.cells, [v.columnId]: v.value ?? '' } } : r)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    // the Holds view and Loading follow the edit at once. The grid itself is only marked stale: the
    // optimistic cell above already shows the edit, and refetching thousands of rows per cell is too slow.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['loading'] });
      void qc.invalidateQueries({ queryKey: ['pdi'], predicate: (q) => q.queryKey[1] !== 'sheet' });
      void qc.invalidateQueries({ queryKey: ['pdi', 'sheet'], refetchType: 'none' });
    },
  });
}

// ── schedule (Gantt) ──────────────────────────────────────────────────────────
export function useSchedule(season: string) {
  return useQuery({
    queryKey: ['schedule', season],
    queryFn: () => api.get<{ vessels: ScheduleVessel[]; tasks: ScheduleTask[] }>(`/api/schedule?season=${encodeURIComponent(season)}`),
    enabled: !!season,
  });
}

function invalidateSchedule(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['schedule'] });
}

export function useSaveScheduleVessel(season: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ScheduleVesselInput) => api.post<{ id: string }>(`/api/schedule/vessel?season=${encodeURIComponent(season)}`, v),
    onSuccess: () => invalidateSchedule(qc),
  });
}
export function useDeleteScheduleVessel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post('/api/schedule/vessel-delete', { id }),
    onSuccess: () => invalidateSchedule(qc),
  });
}
export function useSaveScheduleTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ScheduleTaskInput) => api.post<{ id: string }>('/api/schedule/task', v),
    onSuccess: () => invalidateSchedule(qc),
  });
}
export function useDeleteScheduleTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post('/api/schedule/task-delete', { id }),
    onSuccess: () => invalidateSchedule(qc),
  });
}

export interface ScheduleImportResult {
  addedVessels: number;
  updatedVessels: number;
  addedTasks: number;
  updatedTasks: number;
  removedTasks: number;
}
export function useImportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { saps: string[]; autoTasks: boolean; season?: string }) =>
      api.post<ScheduleImportResult>('/api/schedule/import', v),
    onSuccess: () => invalidateSchedule(qc),
  });
}

// ── agenda ────────────────────────────────────────────────────────────────────
export function useAgenda(date: string) {
  return useQuery({
    queryKey: ['agenda', date],
    queryFn: () => api.get<{ entries: AgendaEntry[]; note: string }>(`/api/agenda?date=${date}`),
  });
}
/** entries over a date window (Dashboard "next 7 days"). keyed under ['agenda', …] so agenda
 *  saves/deletes (which invalidate ['agenda']) refresh it too. */
export function useAgendaUpcoming(from: string, to: string) {
  return useQuery({
    queryKey: ['agenda', 'upcoming', from, to],
    queryFn: () => api.get<{ entries: AgendaEntry[] }>(`/api/agenda/upcoming?from=${from}&to=${to}`),
  });
}
export function useAgendaNotesLog() {
  return useQuery({
    queryKey: ['agenda-notes'],
    queryFn: () => api.get<{ notes: Array<{ date: string; text: string }> }>('/api/agenda/notes'),
  });
}
/** the personal archive (recent notes + entries). keyed under ['agenda', …] so agenda saves/deletes
 *  refresh it too. Only fetched when the Archive tab is open (enabled). */
export function useAgendaArchive(enabled = true) {
  return useQuery({
    queryKey: ['agenda', 'archive'],
    queryFn: () => api.get<{ notes: Array<{ date: string; text: string }>; entries: AgendaEntry[] }>('/api/agenda/archive'),
    enabled,
    staleTime: 30_000,
  });
}
export function useSaveAgendaEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: AgendaEntryInput) => api.post<{ id: string }>('/api/agenda/entry', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] }),
  });
}
export function useDeleteAgendaEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post('/api/agenda/entry-delete', { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] }),
  });
}
export function useSaveAgendaNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { date: string; text: string }) => api.post('/api/agenda/note', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agenda'] });
      qc.invalidateQueries({ queryKey: ['agenda-notes'] });
    },
  });
}

// ── pins ──────────────────────────────────────────────────────────────────────
export function usePins() {
  return useQuery({ queryKey: ['pins'], queryFn: () => api.get<{ pins: Pin[] }>('/api/pins') });
}
export function useAddPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { text: string; color?: string }) => api.post('/api/pins', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pins'] }),
  });
}
export function useDeletePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post('/api/pins/delete', { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pins'] }),
  });
}

// ── sales orders ──────────────────────────────────────────────────────────────
export function useSalesOrders() {
  return useQuery({
    queryKey: ['sales'],
    queryFn: () => api.get<{ orders: SalesOrderSummary[] }>('/api/sales/orders'),
  });
}
export function useSalesOrder(id: string | null) {
  return useQuery({
    queryKey: ['sales', id],
    queryFn: () => api.get<SalesOrder>(`/api/sales/order?id=${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}
export function useSaveSalesOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: SalesOrderInput) => api.post<{ id: string }>('/api/sales/order', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales'] }),
  });
}
export function useDeleteSalesOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post('/api/sales/order-delete', { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales'] }),
  });
}

// ── auth (app-level login) ─────────────────────────────────────────────────────
export function useAuthProviders() {
  return useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api.get<{ google: boolean; microsoft: boolean }>('/api/auth/providers'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api.post('/api/login', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { displayName?: string; avatar?: string | null }) => api.post('/api/profile', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

// ── notifications ───────────────────────────────────────────────────────────────
export interface AppNotification {
  id: number;
  order_id: number | null;
  notification_type: string;
  title: string;
  message: string | null;
  /** the page it opens; null = its event's usual page (notificationLink in @technohull/shared). */
  link?: string | null;
  read_at: string | null;
  created_at: string;
}
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ notifications: AppNotification[]; unread: number }>('/api/notifications'),
    refetchInterval: 60_000,
  });
}

export interface NotifDeptRow { department: string; total: number; unread: number }
/** recent notification activity grouped by department (dashboard box; management:view only). */
export function useNotificationsByDepartment() {
  return useQuery({
    queryKey: ['notifications', 'by-department'],
    queryFn: () => api.get<{ days: number; rows: NotifDeptRow[] }>('/api/notifications/by-department'),
    staleTime: 60_000,
  });
}
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const key = ['notifications'];
  return useMutation({
    meta: COOP_QUIET,
    mutationFn: (v: { id?: number; all?: boolean }) => api.post('/api/notifications/read', v),
    // optimistic — flip read_at + drop the unread count instantly, so clicking a notification doesn't
    // wait on a round-trip + full refetch (the 60s poll reconciles). Mirrors useSetLoadingItem.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ notifications: AppNotification[]; unread: number }>(key);
      if (prev) {
        const now = new Date().toISOString();
        let unread = prev.unread;
        const notifications = prev.notifications.map((n) => {
          if (n.read_at || !(v.all || n.id === v.id)) return n;
          unread = Math.max(0, unread - 1);
          return { ...n, read_at: now };
        });
        qc.setQueryData(key, { ...prev, unread: v.all ? 0 : unread, notifications });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}

// ── loading checklist (D1-backed; vessels come from the Overview; items sync both ways with PDI) ──
export type LoadingResponse = LoadingListResponse;
/** Loading writes share this key: the refresh after a write waits for the last one in flight, so it never
 *  brings back a value an edit made a moment later has already replaced. */
const LOADING_WRITE = ['loading', 'write'];
export function useLoading(enabled = true) {
  return useQuery({
    queryKey: ['loading'],
    queryFn: () => api.get<LoadingResponse>('/api/loading'),
    enabled,
    // PDI changes (made in the app or straight in Smartsheet) land here on the next read
    ...LIVE_SYNC,
  });
}
/** one item. Only the fields given are sent, so a comment edit never re-sends (and re-syncs) the status. */
export function useSetLoadingItem() {
  const qc = useQueryClient();
  const key = ['loading'];
  return useMutation({
    mutationKey: LOADING_WRITE,
    mutationFn: (v: { sap: string; itemNo: number; status?: LoadingStatus; comment?: string; date?: string }) =>
      api.post<LoadingSetResponse>('/api/loading/item', v),
    // optimistic: the change shows at once; the refresh afterwards brings the PDI markers up to date
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<LoadingResponse>(key);
      if (prev) {
        const items = [...prev.items];
        const idx = items.findIndex((x) => x.sap === v.sap && x.itemNo === v.itemNo);
        const cur: LoadingItemState = idx >= 0 ? items[idx]! : { sap: v.sap, itemNo: v.itemNo, status: 'pending', comment: '', date: '', by: '', at: '' };
        const next: LoadingItemState = {
          ...cur,
          ...(v.status !== undefined ? { status: v.status } : {}),
          ...(v.comment !== undefined ? { comment: v.comment } : {}),
          ...(v.date !== undefined ? { date: v.date } : {}),
          by: '',
        };
        if (idx >= 0) items[idx] = next;
        else items.push(next);
        qc.setQueryData<LoadingResponse>(key, { ...prev, items });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => { if (qc.isMutating({ mutationKey: LOADING_WRITE }) === 1) invalidateLoadingAndPdi(qc); },
  });
}

// ── deck flooring (website-only, D1-backed; vessels come from the Overview) ──────────
export interface DeckResponse {
  items: DeckStageState[];
}
export function useDeck() {
  return useQuery({ queryKey: ['deck'], queryFn: () => api.get<DeckResponse>('/api/deck') });
}
export function useSetDeckStage() {
  const qc = useQueryClient();
  const key = ['deck'];
  return useMutation({
    mutationFn: (v: { sap: string; stageNo: number; status: DeckStatus; comment: string; date: string }) =>
      api.post('/api/deck/stage', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<DeckResponse>(key);
      if (prev) {
        const items = [...prev.items];
        const idx = items.findIndex((x) => x.sap === v.sap && x.stageNo === v.stageNo);
        const next: DeckStageState = { sap: v.sap, stageNo: v.stageNo, status: v.status, comment: v.comment, date: v.date, by: '', at: '' };
        if (idx >= 0) items[idx] = { ...items[idx], ...next };
        else items.push(next);
        qc.setQueryData<DeckResponse>(key, { ...prev, items });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}
/** apply one status to every item of a vessel at once (keeps existing comments/dates). */
export function useSetLoadingBulk() {
  const qc = useQueryClient();
  const key = ['loading'];
  return useMutation({
    mutationKey: LOADING_WRITE,
    mutationFn: (v: { sap: string; status: LoadingStatus }) => api.post<LoadingBulkResponse>('/api/loading/bulk', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<LoadingResponse>(key);
      if (prev) {
        const seen = new Set(prev.items.filter((x) => x.sap === v.sap).map((x) => x.itemNo));
        const items = prev.items.map((x) => (x.sap === v.sap ? { ...x, status: v.status } : x));
        for (let i = 0; i < LOADING_ITEMS.length; i++) {
          if (!seen.has(i)) items.push({ sap: v.sap, itemNo: i, status: v.status, comment: '', date: '', by: '', at: '' });
        }
        qc.setQueryData<LoadingResponse>(key, { ...prev, items });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => { if (qc.isMutating({ mutationKey: LOADING_WRITE }) === 1) invalidateLoadingAndPdi(qc); },
  });
}
/** send a boat's Loading changes that haven't reached PDI yet. `includeHolds` also clears PDI holds. */
export function usePushLoadingToPdi() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: LOADING_WRITE,
    mutationFn: (v: { sap: string; includeHolds?: boolean }) => api.post<LoadingPushResponse>('/api/loading/push', v),
    onSettled: () => { if (qc.isMutating({ mutationKey: LOADING_WRITE }) === 1) invalidateLoadingAndPdi(qc); },
  });
}

// ── vessel identification (HIN per SAP; website-only, D1-backed) ─────────────────────
export interface HinRecord {
  sap: string;
  hin: string;
  name: string;
  model: string;
  updatedBy: string;
  updatedAt: string;
}
/** `enabled` off: no request (a page that only sometimes needs the registry, e.g. a vessel's entry form). */
export function useHins(enabled = true) {
  return useQuery({ queryKey: ['hin'], queryFn: () => api.get<{ hins: HinRecord[] }>('/api/hin'), enabled });
}
export interface HinHistoryEntry {
  oldHin: string;
  newHin: string;
  reason: string;
  by: string;
  at: string;
}
/** A SAP's HIN edit history (newest first) fetched lazily — only when a SAP is hovered */
export function useHinHistory(sap: string | null) {
  return useQuery({
    queryKey: ['hin', 'history', sap],
    queryFn: () => api.get<{ history: HinHistoryEntry[] }>(`/api/hin/history?sap=${encodeURIComponent(sap!)}`),
    enabled: !!sap,
    staleTime: 30_000,
  });
}
export function useSetHin() {
  const qc = useQueryClient();
  const key = ['hin'];
  return useMutation({
    mutationFn: (v: { sap: string; hin: string; reason?: string; name?: string }) =>
      api.post<{ ok: boolean; edited: boolean }>('/api/hin', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ hins: HinRecord[] }>(key);
      if (prev) {
        const hins = [...prev.hins];
        const idx = hins.findIndex((x) => x.sap === v.sap);
        const cur = idx >= 0 ? hins[idx] : undefined;
        if (cur) hins[idx] = { ...cur, hin: v.hin };
        else hins.push({ sap: v.sap, hin: v.hin, name: v.name ?? '', model: '', updatedBy: '', updatedAt: '' });
        qc.setQueryData(key, { ...prev, hins });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    // Re-sync from the server so "Last change" (updatedBy/updatedAt) reflects this save.
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
export function useImportHins() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: Array<{ sap: string; hin: string; name?: string; model?: string }>) => api.post<{ imported: number }>('/api/hin/import', { rows }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hin'] }),
  });
}

// ── photos (R2-backed attachments; reused by loading / deck / HIN) ───────────────────
export interface PhotoMeta {
  id: string;
  entity: string;
  refKey: string;
  filename: string;
  uploadedBy: string;
  uploadedAt: string;
}
export function usePhotos(entity: string) {
  return useQuery({
    queryKey: ['photos', entity],
    queryFn: () => api.get<{ items: PhotoMeta[] }>(`/api/photos?entity=${encodeURIComponent(entity)}`),
    staleTime: 30_000,
  });
}
export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { entity: string; refKey: string; file: File }) =>
      api.upload(
        `/api/photos?entity=${encodeURIComponent(v.entity)}&ref=${encodeURIComponent(v.refKey)}&name=${encodeURIComponent(v.file.name)}`,
        v.file,
        v.file.type || 'application/octet-stream',
      ),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['photos', v.entity] }),
  });
}
export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; entity: string }) => api.post('/api/photos/delete', { id: v.id }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['photos', v.entity] }),
  });
}

// ── presence (live "who's on this page") ────────────────────────────────────────
export interface PresenceViewer {
  uid?: string; // opaque id for nudging / messaging / co-op (never the email)
  name: string;
  department?: string; // human label, shown briefly when the viewer joins the page
  avatar?: string | null; // uploaded profile photo (data URI), if any
}
/** heartbeat + poll: posts the current page every 30s and returns the OTHER active viewers.
 *  pauses while the tab is backgrounded, so a hidden tab drops off others' views. */
export function usePresence(path: string) {
  return useQuery({
    queryKey: ['presence', path],
    queryFn: () => api.post<{ viewers: PresenceViewer[] }>('/api/presence', { path }),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: false,
  });
}

// ── info / files (R2) ─────────────────────────────────────────────────────────
export interface FilesResponse {
  prefix: string;
  folders: { prefix: string; name: string }[];
  files: { key: string; name: string; size: number; uploaded: string }[];
}
export function useFiles(prefix: string) {
  return useQuery({
    queryKey: ['files', prefix],
    queryFn: () => api.get<FilesResponse>(`/api/files?prefix=${encodeURIComponent(prefix)}`),
  });
}
export interface SnIndex {
  index: Record<string, { key: string; name: string; uploaded: string }>;
}
/** SAP → its "SN - <sap>.txt" serial-numbers file in the Infocenter (for the Vessel Identification page). */
export function useSnIndex() {
  return useQuery({
    queryKey: ['sn-index'],
    queryFn: () => api.get<SnIndex>('/api/files/sn-index'),
    staleTime: 5 * 60_000,
  });
}
/** create a folder under `prefix` (R2 has no real folders — this writes a .keep marker). */
export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { prefix: string; name: string }) => api.post('/api/files/folder', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}
export function useUploadFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { prefix: string; file: File }) =>
      api.upload(
        `/api/files/upload?prefix=${encodeURIComponent(v.prefix)}&name=${encodeURIComponent(v.file.name)}`,
        v.file,
        v.file.type || 'application/octet-stream',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}
export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.post('/api/files/delete', { key }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

// ── fuel tank S/N (warehouse; website-only, D1-backed. vessels come from the season overview) ──
export interface FuelTankSn {
  sap: string;
  tankNo: number;
  serial: string;
  receiptDate: string;
  floatInstalled: string;
  installDate: string;
  note: string;
  by: string;
  at: string;
}
/** the editable per-tank fields (mirrors the warehouse Excel columns). */
export type FuelTankFields = Pick<FuelTankSn, 'serial' | 'receiptDate' | 'floatInstalled' | 'installDate' | 'note'>;
const EMPTY_TANK_FIELDS: FuelTankFields = { serial: '', receiptDate: '', floatInstalled: '', installDate: '', note: '' };

export function useFuelTanks() {
  return useQuery({ queryKey: ['fuel-tanks'], queryFn: () => api.get<{ items: FuelTankSn[] }>('/api/fuel-tanks') });
}
export function useSetFuelTank() {
  const qc = useQueryClient();
  const key = ['fuel-tanks'];
  return useMutation({
    mutationFn: (v: { sap: string; tankNo: number } & FuelTankFields) => api.post('/api/fuel-tanks/set', v),
    // optimistic — the edit shows instantly; sync/refetch is not needed per keystroke
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ items: FuelTankSn[] }>(key);
      if (prev) {
        const items = [...prev.items];
        const idx = items.findIndex((x) => x.sap === v.sap && x.tankNo === v.tankNo);
        const next: FuelTankSn = { ...EMPTY_TANK_FIELDS, ...v, by: '', at: '' };
        if (idx >= 0) items[idx] = { ...items[idx], ...next };
        else items.push(next);
        qc.setQueryData<{ items: FuelTankSn[] }>(key, { ...prev, items });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}
export function useImportFuelTanks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { items: Array<{ sap: string } & FuelTankFields> }) =>
      api.post<{ ok: boolean; count: number }>('/api/fuel-tanks/import', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fuel-tanks'] }),
  });
}
export function useDeleteFuelTank() {
  const qc = useQueryClient();
  const key = ['fuel-tanks'];
  return useMutation({
    mutationFn: (v: { sap: string; tankNo: number }) => api.post('/api/fuel-tanks/delete', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ items: FuelTankSn[] }>(key);
      if (prev) {
        qc.setQueryData<{ items: FuelTankSn[] }>(key, {
          ...prev,
          items: prev.items.filter((x) => !(x.sap === v.sap && x.tankNo === v.tankNo)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
}

// ── vessel status (warehouse book, D1-backed editable grid) ────────────────────
export interface VesselStatusSheet {
  name: string;
  rows: string[][];
  fills?: string[][]; // parallel grid of hex fill colours ('' = none)
  dateCols?: number[]; // column indices rendered as date pickers
}
export interface VesselStatusDoc {
  sheets: VesselStatusSheet[];
  importedAt: string;
  importedBy: string;
}
export function useVesselStatus() {
  return useQuery({
    queryKey: ['vessel-status'],
    queryFn: () => api.get<VesselStatusDoc>('/api/vessel-status'),
  });
}
export function useSaveVesselStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sheets: VesselStatusSheet[] }) => api.post('/api/vessel-status', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vessel-status'] }),
  });
}
